"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.redirect = void 0;
const create_error_1 = require("../create-error");
const error_interfaces_1 = require("../types/error.interfaces");
const http_interfaces_1 = require("../types/http.interfaces");
/**
 * Redirect the request using redirect policy
 * - Limits number of redirects to maxRedirects
 * - Decorates a request with provided redirect headers
 * - Redirects on provided list of redirect codes or 404 by default
 * - On redirect, notifies Endpoint on new base URL through onRedirect callback
 */
const redirect = (send, endpoint, endpointsSet) => {
    if (!endpoint.redirectPolicy) {
        return send;
    }
    const { maxRedirects, useAbsoluteRedirect, headers, redirectOn, onRedirect } = endpoint.redirectPolicy;
    return async (request) => {
        const redirectRequest = {
            ...request,
            headers: {
                ...request.headers,
                ...headers,
            },
        };
        let response = await send(redirectRequest);
        for (let count = 0;; count++) {
            const { status, headers: { location }, } = response;
            if (count < maxRedirects && shouldRedirect(status, redirectOn)) {
                try {
                    let redirectUrl;
                    // redirect URL can either be returned from the service in the "location" response header
                    // or redirect policy can build a custom redirect URL by providing an `onRedirect` callback.
                    // `onRedirect` may use location value (packages\services\io\services-io-chat\src\chat-service-endpoints.ts),
                    // or generate redirect URL by other means (packages\services\io\services-io-broadcast-attendee\src\broadcast-attendee-endpoints.ts)
                    // Since `location` is optional, `onRedirect` implementations that depend on it, should return undefined when it's missing,
                    // to signal EM that it is a valid error response, and no redirect is necessary.
                    if (onRedirect) {
                        redirectUrl = onRedirect(location, status);
                    }
                    else if (location) {
                        redirectUrl = new URL(location);
                    }
                    // we were not able to construct redirect URL.
                    // return original failed response
                    if (!redirectUrl) {
                        return response;
                    }
                    if (useAbsoluteRedirect) {
                        // Update both baseUrl and relative url
                        endpointsSet.baseUrl = redirectUrl.origin;
                        redirectRequest.url = redirectUrl.pathname;
                    }
                    else {
                        // Reset absolute URL to an original relative URL to let a new location
                        redirectRequest.url = request.url;
                        // Only update baseUrl and use relative URL from endpoint
                        endpointsSet.baseUrl = redirectUrl.href;
                    }
                }
                catch (e) {
                    throw (0, create_error_1.createError)(error_interfaces_1.ErrorReason.InvalidUrl, undefined, e);
                }
                redirectRequest.operationLogger.nextStep(http_interfaces_1.OperationLogEntryType.Redirect, { result: http_interfaces_1.Status[response.status] }, redirectRequest, response);
                response = await send(redirectRequest);
            }
            else {
                return response;
            }
        }
    };
};
exports.redirect = redirect;
/** Checks whether response code is matching one in provided list of codes to redirect on */
function shouldRedirect(responseCode, redirectOn = http_interfaces_1.Status.NOT_FOUND) {
    return Array.isArray(redirectOn)
        ? redirectOn.some((status) => matchStatus(responseCode, status))
        : matchStatus(responseCode, redirectOn);
}
/** Checks whether response code is matching redirectOn code */
function matchStatus(responseCode, redirectOn) {
    return typeof redirectOn === "string"
        ? new RegExp(redirectOn.replace(/x/gi, "\\d")).test(responseCode.toString())
        : responseCode === redirectOn;
}
